/**
 * Centralised TanStack Query keys. Build every key from here so cache
 * invalidation stays refactor-safe.
 *
 * For tenant-scoped resources the active scope ('all' | tenantId) is the first
 * discriminating segment of the list key — invalidating `[resource, scope]`
 * clears just that scope, and keys are distinct per scope by construction.
 * `<resource>.all` is the broad prefix for `invalidateQueries`.
 */
type Params = Record<string, unknown>

export const queryKeys = {
  session: ['session'] as const,

  tenants: {
    all: ['tenants'] as const,
    list: (params?: Params) => ['tenants', 'list', params ?? {}] as const,
    detail: (id: string) => ['tenants', 'detail', id] as const,
  },

  topics: {
    all: ['topics'] as const,
    list: (scope: string, params?: Params) => ['topics', scope, 'list', params ?? {}] as const,
    detail: (id: string) => ['topics', 'detail', id] as const,
    cluster: (id: string) => ['topics', 'cluster', id] as const,
    articles: (id: string, params?: Params) => ['topics', 'articles', id, params ?? {}] as const,
  },

  articles: {
    all: ['articles'] as const,
    list: (scope: string, params?: Params) => ['articles', scope, 'list', params ?? {}] as const,
    detail: (id: string) => ['articles', 'detail', id] as const,
    generation: (id: string) => ['articles', 'generation', id] as const,
    generationEvents: (id: string) => ['articles', 'generationEvents', id] as const,
    preview: (id: string) => ['articles', 'preview', id] as const,
    revisions: (id: string) => ['articles', 'revisions', id] as const,
    revision: (id: string, n: number) => ['articles', 'revision', id, n] as const,
    translations: (id: string) => ['articles', 'translations', id] as const,
    calendar: (scope: string, from?: string, to?: string) =>
      ['articles', scope, 'calendar', from ?? null, to ?? null] as const,
  },

  webhookEndpoints: {
    all: ['webhookEndpoints'] as const,
    list: (scope: string, params?: Params) =>
      ['webhookEndpoints', scope, 'list', params ?? {}] as const,
    detail: (id: string) => ['webhookEndpoints', 'detail', id] as const,
  },

  webhookDeliveries: {
    all: ['webhookDeliveries'] as const,
    list: (scope: string, params?: Params) =>
      ['webhookDeliveries', scope, 'list', params ?? {}] as const,
  },

  auditLogs: {
    all: ['auditLogs'] as const,
    list: (scope: string, params?: Params) => ['auditLogs', scope, 'list', params ?? {}] as const,
  },

  researchRuns: {
    all: ['researchRuns'] as const,
    list: (scope: string, params?: Params) =>
      ['researchRuns', scope, 'list', params ?? {}] as const,
    detail: (id: string) => ['researchRuns', 'detail', id] as const,
  },

  localeAudit: (tenantId: string) => ['localeAudit', tenantId] as const,

  siteAudit: (tenantId: string) => ['siteAudit', tenantId] as const,

  seoGeo: (tenantId: string) => ['seoGeo', tenantId] as const,
  seoSpend: (tenantId: string) => ['seoSpend', tenantId] as const,
  seoPlatform: ['seoPlatform'] as const,

  health: ['health'] as const,
  healthJobs: ['health', 'jobs'] as const,
} as const
